/**
 * When a finding stops applying.
 *
 * A finding is re-derived on every save, so this rule decides whether a
 * complaint the reader raised still describes the document in front of the
 * operator. Getting it wrong in either direction has a name: a rule that clears
 * too eagerly hides a fault, and a rule that never clears traps a document in a
 * queue nobody can empty — no edit helps, because there is nothing about the
 * document to put right.
 *
 * **Why this file exists.** The rule was written twice, once here and once in
 * `ReviewState.StillHolds`, and both copies ended in "anything I do not
 * recognise holds forever". That default produced the same bug twice in one day:
 * `page_rotation_guessed` was taught to clear on this side and not on the
 * engine's, and `misplaced_value` the same. Each was fixed as its own special
 * case, which left the next code to be added to fall into the identical trap.
 *
 * The answer is not a third special case. It is that **every code in the
 * vocabulary must state its rule, and the statement is checked**:
 *
 * - `HOLD_RULES` is a `Record` over `FindingCode`, so a code added to `FINDING`
 *   without a rule here does not compile.
 * - `ReviewState.StillHolds` is a table over the same vocabulary, and its suite
 *   fails if a `FindingCode` constant has no entry.
 * - Both sides answer the same cases in
 *   `tests/shared-rules/finding-clearing-cases.json`, so a rule that drifts on
 *   one side fails the build rather than showing an operator a cleared checklist
 *   beside a document the engine is still holding.
 *
 * **The two kinds of finding, stated once.** Some findings are a record of what
 * happened to a page — `page_rotation_applied` says a sideways or upside-down
 * page was turned, and that does not stop being true because the document was
 * saved again. Those hold permanently and say so. The rest are complaints about
 * a repairable state, and each is tied to the state it complains about, so
 * answering the complaint clears it and only that. Permanence is a decision a
 * code makes on purpose here, never a fall-through.
 */

import { FINDING, MISPLACED_VALUE_SOURCE } from './desktop-api';

/** A code the reader can raise. Exactly the values of `FINDING`. */
export type FindingCode = (typeof FINDING)[keyof typeof FINDING];

/** The parts of a field any clearing rule is allowed to look at. */
export type ClearingField = {
  value?: string;
  needsReview?: boolean;
  verified?: boolean;
  source?: string;
  evidenceLineIndexes?: readonly number[] | null;
  barcodeEvidence?: unknown;
  lookupEvidence?: unknown;
};

/** The parts of a document any clearing rule is allowed to look at. */
export type ClearingDocument = {
  fields: readonly ClearingField[];
  lineItems?: readonly {
    quantity?: number | null;
    unitPrice?: number | null;
    amount?: number | null;
    arithmeticValidated?: boolean;
    verified?: boolean;
  }[];
  pages?: readonly { notReadReason?: string | null }[];
  documentTypeVerified?: boolean;
  confirmedAsWhole?: boolean;
  notReadReason?: string | null;
};

/**
 * A value nothing on the page supports.
 *
 * A value from a lookup table, typed by an operator, or vouched for is not
 * expected to be on the page and is not ungrounded. Written once because it was
 * written twice in the renderer and a third time in the engine, and the three
 * had to agree for a document's warning to match its checklist.
 */
export const valueIsUngrounded = (field: ClearingField): boolean =>
  Boolean(field.value?.trim()) &&
  !field.evidenceLineIndexes?.length &&
  !field.barcodeEvidence &&
  !field.lookupEvidence &&
  !field.source?.toLowerCase().startsWith('operator') &&
  !field.verified;

type ClearingFinding = { code?: string; message?: string };

// Row verification answers the displayed cells, not whether a missing table is
// now complete. The whole-document answer supplies that second assertion.
const hasConfirmedReplacementTable = (document: ClearingDocument): boolean =>
  document.confirmedAsWhole === true &&
  (document.pages?.length ?? 0) > 0 &&
  (document.lineItems?.length ?? 0) > 0 &&
  document.lineItems?.every((row) => row.verified === true) === true &&
  !document.notReadReason?.trim() &&
  !(document.pages ?? []).some((page) => Boolean(page.notReadReason?.trim()));

/**
 * One rule per code, and the compiler requires all of them.
 *
 * Adding a code to `FINDING` without adding it here is a type error naming the
 * missing key. That is the whole mechanism: the decision cannot be deferred to a
 * default, because there is no default.
 */
const HOLD_RULES: Record<
  FindingCode,
  (document: ClearingDocument, finding: ClearingFinding) => boolean
> = {
  [FINDING.lineItemsPossiblyIncomplete]: (document) => !hasConfirmedReplacementTable(document),
  // This is a catch-all code, including real scalar and processing failures.
  // Only this exact legacy FastTableMapper complaint is answered by a table.
  [FINDING.partialSemanticProcessing]: (document, finding) =>
    finding.message?.startsWith('Template extraction was unavailable; product-shape retry used: ')
      ? !(
          document.confirmedAsWhole &&
          document.documentTypeVerified &&
          (document.fields ?? []).length > 0 &&
          (document.fields ?? []).every((field) => field.verified) &&
          !document.notReadReason?.trim() &&
          (document.pages ?? []).length > 0 &&
          (document.pages ?? []).every((page) => !page.notReadReason?.trim())
        )
      : finding.message !==
          'No line-item rows were mapped from explicit table headings. Review the document.' ||
        !hasConfirmedReplacementTable(document),
  // `arithmeticValidated` is read, not recomputed, and that is the whole point of
  // it: it carries either "the numbers agree" or "the operator looked at a row
  // that does not add up and said it is right as printed". The second cannot be
  // derived from the numbers. This side used to recompute the sum and ignore the
  // flag, so answering the question put the document straight back into the queue
  // it had just been cleared from — while the engine, which honoured the flag,
  // considered it settled.
  // The flag alone, as ReviewState.ArithmeticIsOff: the engine never recomputes the
  // multiplication. Releasing a row here because it computes as balanced cleared a
  // document the engine still held, leaving the operator nothing to answer. Such a
  // row is no question on screen (review-main-action.ts, openMathRow), and the
  // whole-document confirmation records that its numbers agree.
  [FINDING.lineItemArithmetic]: (document) =>
    (document.lineItems ?? []).some(
      (row) =>
        row.quantity != null &&
        row.unitPrice != null &&
        row.amount != null &&
        !row.arithmeticValidated,
    ),

  // A refused closer read retains every initial row. Confirming the remaining
  // rows (or removing ones the page never printed) is the operator's answer.
  [FINDING.lineItemReadIssue]: (document) =>
    (document.lineItems ?? []).some((row) => !row.verified),

  [FINDING.ungroundedExtraction]: (document) => document.fields.some(valueIsUngrounded),

  // The individual fields are the work. Once every marked field has been
  // answered, keeping the broad reminder would leave an unfinishable checklist
  // item with no field to visit.
  [FINDING.semanticAliasConflict]: (document) =>
    document.fields.some((field) => field.needsReview && !field.verified),

  // A page whose direction could not be confirmed asks the operator to check the
  // column order against the page. Confirming the document IS that check, so the
  // question is answered and the sentence stops applying.
  [FINDING.pageRotationGuessed]: (document) => !document.documentTypeVerified,

  // A record of what happened, not a complaint: the page really was turned, and
  // it stays turned. Informational severity, so holding it permanently does not
  // hold the document in review — it keeps the note attached to the page it is
  // about. This is the one code for which "always true" is the intended answer.
  [FINDING.pageRotationApplied]: () => true,

  // A value set aside because it sits under another field's label leaves the
  // field empty and asks the operator to judge it. Answering the field — typing
  // a value or vouching for the blank — is that judgement, so the complaint
  // stops applying. Left to stand, the one finding whose whole purpose is to
  // send an operator back to the page would still be there after they went.
  [FINDING.misplacedValue]: (document) =>
    document.fields.some(
      (field) => field.source === MISPLACED_VALUE_SOURCE && !field.value?.trim() && !field.verified,
    ),

  // "This was never read" stops being true the moment a reading reaches the
  // model, and not one save earlier. Tied to the flag rather than left to stand
  // forever, so a document re-read successfully sheds the complaint and one
  // re-read against a runtime that is still down keeps it.
  [FINDING.modelUnreachable]: (document) => Boolean(document.notReadReason?.trim()),
  [FINDING.modelPartiallyUnreachable]: (document) => Boolean(document.notReadReason?.trim()),

  // Asked of the PAGES, not of a flag beside them, because the pages are what
  // the sentence is about. Nothing an operator types clears this and nothing
  // should: the missing text is the fault, and it is not on the document to be
  // corrected.
  [FINDING.pageNotRead]: (document) =>
    (document.pages ?? []).some((page) => Boolean(page.notReadReason?.trim())),
};

/**
 * The codes the engine's DocumentPipeline raises by their literal name rather
 * than as a `FindingCode` constant (2026-10-16). They reach a
 * document just the same, so each states its rule too, as `ReviewState.HoldRules`
 * does for the same names.
 */
const NAMED_HOLD_RULES: Record<
  string,
  (document: ClearingDocument, finding: ClearingFinding) => boolean
> = {
  // Two readings disagree about a field, or about the document's outcome.
  // Confirming the document as a whole is the answer to which one is right.
  'explicit-invoice-field-conflict': (document) => document.confirmedAsWhole !== true,
  'explicit-outcome-conflict': (document) => document.confirmedAsWhole !== true,

  // A table recovery the reader was not sure of: confirming the line items
  // answers it, every row (or, with no rows left, the whole document, which
  // confirms the empty table).
  line_item_read_needs_review: (document) =>
    (document.lineItems ?? []).length === 0
      ? document.confirmedAsWhole !== true
      : (document.lineItems ?? []).some((row) => !row.verified),

  // A reading that failed, or values with no field to put them in: nothing an
  // operator types answers either. They stand until the document is read again;
  // a re-read that succeeds writes its own findings in place of these.
  'reading-had-no-field': () => true,
  semantic_processing_failed: () => true,

  // Pages left unread for fields: no confirmation of what was read replaces the
  // missing reading. Held until a re-read; stated here so the permanence is a decision.
  pages_not_read_for_fields: () => true,
  // A table page left unread, or a table reader that failed: rows may be missing,
  // so it is answered the way a missing table is, by a person who checked every
  // row and confirmed the whole document (the table then is the one they checked
  // against the pages). Held otherwise; a re-read that succeeds replaces it. Before
  // 2026-11-01 nothing a person did answered it, and the document stayed held.
  pages_not_read_for_line_items: (document) => !hasConfirmedReplacementTable(document),
  line_item_processing_failed: (document) => !hasConfirmedReplacementTable(document),
};

/**
 * Does this finding still describe the document as it now stands?
 *
 * A code this build has never heard of holds, which is the safe direction for a
 * complaint — but it is a real gap rather than a design, and it can only happen
 * if the engine raises a code that is in neither `FINDING` nor the named codes.
 * The vocabulary check in `tests/finding-clearing-tests` exists to make sure that
 * never ships.
 */
export const findingStillHolds = (
  finding: ClearingFinding,
  document: ClearingDocument,
): boolean => {
  const rule =
    HOLD_RULES[finding.code as FindingCode] ??
    (finding.code !== undefined &&
    Object.prototype.hasOwnProperty.call(NAMED_HOLD_RULES, finding.code)
      ? NAMED_HOLD_RULES[finding.code]
      : undefined);
  return rule ? rule(document, finding) : true;
};

/** Every `FINDING` code this side knows how to clear. Used by the vocabulary check. */
export const CLEARING_VOCABULARY: readonly string[] = Object.keys(HOLD_RULES);

/** Every code the engine raises by its literal name that this side knows how to clear. Used by the vocabulary check. */
export const NAMED_CLEARING_VOCABULARY: readonly string[] = Object.keys(NAMED_HOLD_RULES);
